import React from "react";
import { Footer, Navbar } from "../components";
import AdsBannerTop from "../components/AdsBannerTop";
import { Film, Tv, Download, Star, Shield, Zap, Users, Heart, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { useSpring, animated } from "react-spring";
import { useState } from "react";

const AccordionItem = ({ title, content }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { height, opacity } = useSpring({
    from: { height: 0, opacity: 0 },
    to: { height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 },
  });

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="border-b border-white/10 last:border-b-0 overflow-hidden">
      <div
        className="flex items-center justify-between cursor-pointer p-5 md:p-6 hover:bg-gray/20 transition-all duration-300 group"
        onClick={toggleAccordion}
      >
        <h3 className="text-lg md:text-xl text-white poppins font-semibold pr-4">
          {title}
        </h3>
        <div className="flex-shrink-0">
          <animated.div
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
            }}
          >
            <ChevronDown className="text-white/60 group-hover:text-red transition-colors duration-300" size={24} />
          </animated.div>
        </div>
      </div>
      <animated.div
        style={{ height, opacity }}
        className="overflow-hidden transition-all duration-300"
      >
        <p className="p-5 md:p-6 pt-0 text-white/80 mont text-base md:text-lg leading-relaxed">
          {content}
        </p>
      </animated.div>
    </div>
  );
};

export default function About() {
  const reasonsData = [
    { 
      id: 1, 
      icon: Zap,
      title: "It's Free, Easy, and Quick!",
      description: "Access unlimited entertainment without any subscription fees or hidden costs"
    },
    { 
      id: 2, 
      icon: Star,
      title: "Exclusive Access",
      description: "Get early access to the latest releases and exclusive content"
    },
    { 
      id: 3, 
      icon: Sparkles,
      title: "Personalized Recommendations",
      description: "Discover content tailored to your preferences and viewing history"
    },
    {
      id: 4,
      icon: Download,
      title: "Stream & Download",
      description: "Watch movies, TV shows, cartoons and trailers anytime, anywhere"
    },
  ];

  const features = [
    {
      icon: Film,
      title: "Massive Library",
      description: "Thousands of movies and TV shows at your fingertips"
    },
    {
      icon: Shield,
      title: "Safe & Secure",
      description: "Your privacy and security are our top priorities"
    },
    {
      icon: Users,
      title: "Family Friendly",
      description: "Content suitable for all ages and family viewing"
    },
    {
      icon: Heart,
      title: "User Focused",
      description: "Built with your entertainment needs in mind"
    },
  ];

  return (
    <div className="bg-black min-h-screen">
      <Navbar />
      <AdsBannerTop />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-24 md:pt-32 pb-16 md:pb-24">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-red/20 via-black to-black"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
        
        <div className="relative z-10 padding">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-1 w-12 bg-red"></div>
              <span className="text-red mont font-semibold text-sm uppercase tracking-wider">
                About Us
              </span>
              <div className="h-1 w-12 bg-red"></div>
            </div>
            <h1 className="text-white poppins font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight mb-6">
              Welcome to <span className="text-red">FilmJetHub</span>
            </h1>
            <p className="text-white/80 mont text-lg md:text-xl leading-relaxed max-w-3xl mx-auto">
              Your ultimate destination for free, unlimited streaming of movies, TV shows, cartoons, and exclusive trailers. 
              Experience the magic of cinema without boundaries.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="padding py-16 md:py-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-1 w-12 bg-red"></div>
                <span className="text-red mont font-semibold text-sm uppercase tracking-wider">
                  Our Mission
                </span>
              </div>
              <h2 className="text-white poppins text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                Making Entertainment Accessible to Everyone
              </h2>
              <p className="text-white/80 mont text-base md:text-lg leading-relaxed mb-4">
                At FilmJetHub, we believe that great entertainment should be accessible to everyone, regardless of their financial situation. 
                Our mission is to provide a platform where users can discover, stream, and enjoy a vast library of content completely free of charge.
              </p>
              <p className="text-white/80 mont text-base md:text-lg leading-relaxed">
                We're committed to delivering an exceptional viewing experience with high-quality streaming, intuitive navigation, 
                and a constantly expanding collection of the latest releases and timeless classics.
              </p>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-red/20 to-red/5 rounded-2xl p-8 md:p-12 border border-white/10 backdrop-blur-sm">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-red poppins font-bold text-4xl mb-2">10K+</div>
                    <div className="text-white/60 mont text-sm">Movies</div>
                  </div>
                  <div className="text-center">
                    <div className="text-red poppins font-bold text-4xl mb-2">5K+</div>
                    <div className="text-white/60 mont text-sm">TV Shows</div>
                  </div>
                  <div className="text-center">
                    <div className="text-red poppins font-bold text-4xl mb-2">100%</div>
                    <div className="text-white/60 mont text-sm">Free</div>
                  </div>
                  <div className="text-center">
                    <div className="text-red poppins font-bold text-4xl mb-2">24/7</div>
                    <div className="text-white/60 mont text-sm">Available</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why FilmJetHub Section */}
      <section className="padding py-16 md:py-24 bg-gray/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-1 w-12 bg-red"></div>
              <span className="text-red mont font-semibold text-sm uppercase tracking-wider">
                Why Choose Us
              </span>
              <div className="h-1 w-12 bg-red"></div>
            </div>
            <h2 className="text-white poppins text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Why FilmJetHub?
            </h2>
            <p className="text-white/60 mont text-lg max-w-2xl mx-auto">
              Discover what makes FilmJetHub the perfect choice for your entertainment needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {reasonsData.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.id}
                  className="group relative bg-gray/20 backdrop-blur-sm rounded-lg p-6 border border-white/10 hover:border-red/30 transition-all duration-300 hover:transform hover:scale-105"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-red/0 via-red/0 to-red/0 group-hover:from-red/5 group-hover:via-red/5 group-hover:to-red/5 transition-all duration-300 rounded-lg"></div>
                  
                  <div className="relative z-10">
                    <div className="w-14 h-14 rounded-lg bg-red/20 flex items-center justify-center mb-4 group-hover:bg-red/30 transition-colors duration-300">
                      <Icon className="text-red" size={28} />
                    </div>
                    <h3 className="text-white poppins font-semibold text-xl mb-2">
                      {item.title}
                    </h3>
                    <p className="text-white/60 mont text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="padding py-16 md:py-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-1 w-12 bg-red"></div>
              <span className="text-red mont font-semibold text-sm uppercase tracking-wider">
                Features
              </span>
              <div className="h-1 w-12 bg-red"></div>
            </div>
            <h2 className="text-white poppins text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              What We Offer
            </h2>
            <p className="text-white/60 mont text-lg max-w-2xl mx-auto">
              Everything you need for the ultimate streaming experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group bg-gray/20 backdrop-blur-sm rounded-lg p-6 border border-white/10 hover:border-red/30 transition-all duration-300"
                >
                  <Icon className="text-red mb-4 group-hover:scale-110 transition-transform duration-300" size={32} />
                  <h3 className="text-white poppins font-semibold text-lg mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-white/60 mont text-sm">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="padding py-16 md:py-24 bg-gray/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-1 w-12 bg-red"></div>
              <span className="text-red mont font-semibold text-sm uppercase tracking-wider">
                FAQ
              </span>
              <div className="h-1 w-12 bg-red"></div>
            </div>
            <h2 className="text-white poppins text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-white/60 mont text-lg max-w-2xl mx-auto">
              Find answers to common questions about FilmJetHub
            </p>
          </div>

          <div className="bg-gray/10 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden">
            <AccordionItem
              title="What is FilmJetHub?"
              content="FilmJetHub is the go-to platform for an unparalleled entertainment experience. Dive into an extensive selection of movies, TV shows, and cartoons, all at your fingertips. Discover new releases, binge-watch your favorite series, and catch exclusive trailers. Your one-stop hub for cinematic adventures and thrilling previews."
            />
            <AccordionItem
              title="Is FilmJetHub completely free?"
              content="Yes, at FilmJetHub, your cinematic journey comes with a delightful perk—complete freedom. Enjoy unlimited access to our vast library of movies, TV shows, and trailers entirely for free. No subscription fees, no hidden costs—just pure entertainment at your fingertips. FilmJetHub believes in making the magic of movies accessible to all. Dive into the world of entertainment without breaking the bank."
            />
            <AccordionItem
              title="Is FilmJetHub good for kids?"
              content="Absolutely! FilmJetHub is a family-friendly haven, offering a diverse selection of content suitable for all ages. Our platform ensures a safe and enjoyable viewing experience for kids, featuring a wide array of cartoons, animated films, and educational shows. Parents can trust FilmJetHub to provide entertaining and enriching content that aligns with their family values. Join us in creating lasting memories as your little ones embark on exciting adventures through our curated collection."
            />
            <AccordionItem
              title="Is FilmJetHub available on multiple devices?"
              content="Absolutely! FilmJetHub provides a seamless streaming experience across various devices, including computers, tablets, and smart TVs. You can also download our mobile app for iOS and Android devices to enjoy your favorite content on the go."
            />
            <AccordionItem
              title="Can I watch movies offline on FilmJetHub?"
              content="Currently, FilmJetHub focuses on online streaming, and offline viewing is not supported. However, you can stream content anytime as long as you have an internet connection."
            />
            <AccordionItem
              title="Does FilmJetHub offer subtitles for movies and shows?"
              content="Yes, FilmJetHub typically provides subtitles for a variety of languages, enhancing accessibility for a global audience. Subtitles are available in multiple languages to ensure everyone can enjoy the content."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="padding py-16 md:py-24">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-gray/20 to-gray/10 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-white/10">
            <Sparkles className="text-red mx-auto mb-6" size={64} />
            <h2 className="text-white poppins text-3xl md:text-4xl font-bold mb-4">
              Ready to Start Streaming?
            </h2>
            <p className="text-white/70 mont text-lg mb-8 max-w-2xl mx-auto">
              Join thousands of users enjoying unlimited free entertainment. Start exploring our vast library of movies and TV shows today!
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="/"
                className="px-8 py-3 bg-red text-white mont font-semibold text-lg rounded-lg hover:bg-red/90 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-red/30"
              >
                Browse Movies
              </a>
              <a
                href="/tvshows"
                className="px-8 py-3 bg-gray/30 text-white mont font-semibold text-lg rounded-lg border border-white/20 hover:bg-gray/40 transition-all duration-300 transform hover:scale-105"
              >
                Browse TV Shows
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
